import type { CSSProperties } from 'react';
import './MasteryHeatmap.css';

export interface HeatmapCell {
  /** 0..1 mastery. */
  mastery: number;
  flag?: boolean;
  label?: string;
}

export interface MasteryHeatmapProps {
  cells: ReadonlyArray<HeatmapCell | number>;
  columns?: number;
  cellSize?: number;
  onCellClick?: (index: number, cell: HeatmapCell | number) => void;
  style?: CSSProperties;
}

function fillFor(m: number | null | undefined): string {
  if (m == null) return 'var(--ink-750)';
  const t = Math.max(0, Math.min(1, m));
  return `color-mix(in oklab, var(--accent) ${Math.round(t * 100)}%, var(--ink-750))`;
}

export function MasteryHeatmap({
  cells,
  columns = 8,
  cellSize = 22,
  onCellClick,
  style,
}: MasteryHeatmapProps) {
  // Accessible alternative: a screen-reader-only table of the same data (WCAG 2.2 AA —
  // graph/trace visualizations need a textual/table equivalent).
  return (
    <div className="uc-hm" style={style}>
      <table className="uc-sr-only">
        <caption>Topic mastery</caption>
        <thead>
          <tr>
            <th>Topic</th>
            <th>Mastery</th>
            <th>Open misconception</th>
          </tr>
        </thead>
        <tbody>
          {cells.map((c, i) => {
            const m = typeof c === 'number' ? c : c.mastery;
            const label = typeof c === 'object' ? (c.label ?? `cell ${i + 1}`) : `cell ${i + 1}`;
            const flag = typeof c === 'object' && c.flag;
            return (
              <tr key={i}>
                <td>{label}</td>
                <td>{Math.round((m || 0) * 100)}%</td>
                <td>{flag ? 'yes' : 'no'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div
        className="uc-hm__grid"
        aria-hidden="true"
        style={{ gridTemplateColumns: `repeat(${columns}, ${cellSize}px)` }}
      >
        {cells.map((c, i) => {
          const m = typeof c === 'number' ? c : c.mastery;
          const flag = typeof c === 'object' && c.flag ? '1' : '0';
          const label = typeof c === 'object' ? c.label : undefined;
          const pct = Math.round((m || 0) * 100);
          return (
            <div
              key={i}
              className={`uc-hm__cell${onCellClick ? ' uc-hm__cell--int' : ''}`}
              data-flag={flag}
              style={{ background: fillFor(m), width: cellSize, height: cellSize }}
              title={label ? `${label} · ${pct}%` : `${pct}%`}
              onClick={onCellClick ? () => onCellClick(i, c) : undefined}
            />
          );
        })}
      </div>
      <div className="uc-hm__legend">
        <span>weak</span>
        <span className="uc-hm__swatch" style={{ background: fillFor(0.1) }} />
        <span className="uc-hm__swatch" style={{ background: fillFor(0.45) }} />
        <span className="uc-hm__swatch" style={{ background: fillFor(0.8) }} />
        <span className="uc-hm__swatch" style={{ background: fillFor(1) }} />
        <span>
          strong · <span style={{ color: 'var(--incorrect)' }}>●</span> open misconception
        </span>
      </div>
    </div>
  );
}
